// Dependencias
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Componentes
import BannerFichas from '../../../components/BannerFichas/BannerFichas';
import Navbar from '../../../components/Navbar/Navbar';
import SessionBar from '../../../components/SessionBar/SessionBar';
import Footer from '../../../components/Footer/Footer';
import CardCompraSaldo from '../../../components/CardCompraSaldo/CardCompraSaldo';
import BotonGrandeDinamico from '../../../components/Botones/BotonGrandeDinamico/BotonGrandeDinamico';

// Funciones
import { ROUTES } from '../../../const/routes';
import { fetchUserData } from '../../../utils/api';

const ListaCargaSaldo = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const selectedCurrency = 'ARS';
  const conversionRate = 1;
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  const irACargarSaldoPago = (monto) => {
    navigate(`${ROUTES.eleccionCargaSaldo}?monto=${monto}`);
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div>
      <BannerFichas />
      <div className="bg-gradient-to-b from-slate-950 to-violet-950 min-h-screen">
        <Navbar />
        <SessionBar />

        <div className="flex flex-col flex-grow items-center justify-center text-center py-10 px-4">
          <h2 className="text-2xl text-yellow-500 font-bold mb-4">
            {t('loadBalance.title')}
          </h2>
          <p className="text-sm text-gray-300 mb-8">
            {t('loadBalance.subtitle')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 w-full max-w-6xl">
            <CardCompraSaldo
              imagen={ROUTES.rutaFichasPoker + 'FichaPoker50000Resized.png'}
              valor={formatCurrency(50000 * conversionRate)}
              moneda={selectedCurrency}
              funcion={() => irACargarSaldoPago(50000)}
            />
            <CardCompraSaldo
              imagen={ROUTES.rutaFichasPoker + 'FichaPoker20000Resized.png'}
              valor={formatCurrency(20000 * conversionRate)}
              moneda={selectedCurrency}
              funcion={() => irACargarSaldoPago(20000)}
            />
            <CardCompraSaldo
              imagen={ROUTES.rutaFichasPoker + 'FichaPoker10000Resized.png'}
              valor={formatCurrency(10000 * conversionRate)}
              moneda={selectedCurrency}
              funcion={() => irACargarSaldoPago(10000)}
            />
            <CardCompraSaldo
              imagen={ROUTES.rutaFichasPoker + 'FichaPoker5000Resized.png'}
              valor={formatCurrency(5000 * conversionRate)}
              moneda={selectedCurrency}
              funcion={() => irACargarSaldoPago(5000)}
            />
            <CardCompraSaldo
              imagen={ROUTES.rutaFichasPoker + 'FichaPoker2000Resized.png'}
              valor={formatCurrency(2000 * conversionRate)}
              moneda={selectedCurrency}
              funcion={() => irACargarSaldoPago(2000)}
            />
            <CardCompraSaldo
              imagen={ROUTES.rutaFichasPoker + 'FichaPoker1000Resized.png'}
              valor={formatCurrency(1000 * conversionRate)}
              moneda={selectedCurrency}
              funcion={() => irACargarSaldoPago(1000)}
            />
          </div>
        </div>
      </div>
      <Footer />

      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-slate-900 border-2 border-white/80 rounded-2xl shadow-[0_0_20px_rgba(255,255,255,0.25)] max-w-2xl w-full p-4 sm:p-6 text-gray-200">
            <div className="flex justify-center mb-2">
              <svg
                className="w-8 h-8 text-yellow-400"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 9V13M12 17H12.01M12 3L2 21H22L12 3Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h2 className="text-base sm:text-lg font-bold text-yellow-400 text-center mb-2">
              {t('loadBalance.disclaimerTitle')}
            </h2>

            <ul className="space-y-1.5 text-xs sm:text-sm leading-relaxed list-disc list-inside marker:text-yellow-500">
              <li>
                {t('loadBalance.startBalance1')}{' '}
                <span className="font-semibold text-red-400">
                  {t('loadBalance.notRealCasino')}
                </span>
                . {t('loadBalance.academicProject')}
              </li>
              <li>
                {t('loadBalance.startBalance1')}{' '}
                <span className="font-semibold text-yellow-300">{t('loadBalance.startBalance2')}</span>{' '}
                {t('loadBalance.startBalance3')}
              </li>
              <li>
                {t('loadBalance.resetInfo1')}{' '}
                <span className="font-semibold text-yellow-300">{t('loadBalance.resetInfo2')}</span>{' '}
                {t('loadBalance.resetInfo3')}
              </li>
              <li>
                {t('loadBalance.mercadopago1')}{' '}
                <span className="font-semibold text-yellow-300">
                  {t('loadBalance.mercadopago2')}
                </span>
                . {t('loadBalance.mercadopago3')}
              </li>
              <li>
                {t('loadBalance.testAccounts1')}{' '}
                <a
                  href="https://www.mercadopago.com.ar/developers/es/docs/your-integrations/test/accounts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-300 underline hover:text-yellow-200"
                >
                  {t('loadBalance.testAccounts2')}
                </a>
                {t('loadBalance.testAccounts3')}
              </li>
              <li>
                <span className="font-semibold text-red-400">
                  {t('loadBalance.noRealMoney1')}
                </span>{' '}
                {t('loadBalance.noRealMoney2')}
              </li>
            </ul>

            <div className="mt-4">
              <BotonGrandeDinamico
                texto={t('loadBalance.understoodButton')}
                bgColor="green"
                funcion={() => setShowDisclaimer(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaCargaSaldo;
